import { useCallback, useEffect, useState } from 'react';
import useUserPreference from '../../hooks/useUserPreference';
import styles from './GiideWithStep.module.css';
import image from '../../studio/assets/getStartedModalImage.png';
import { Button, Checkbox, Flex, Modal } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { IState, Step } from '../../graphql/types';
import { setGetStartedModalOpen } from '../../actions';

export const GetStartedModal = () => {
  const dispatch = useDispatch();
  const { preference, updatePreference } = useUserPreference(
    'dontShowGetStartedModal'
  );

  const [open, setOpen] = useState(false);

  const recordingErrorModalVisible = useSelector<IState, boolean>(
    state => state.recordingErrorModalVisible || false
  );
  const recordingProcessingModalVisible = useSelector<IState, boolean>(
    state => !!state.recordingProcessingModalVisible
  );

  const openedStep = useSelector<IState, Step | undefined>(state =>
    typeof state.openedStepOrder === 'number'
      ? state.steps.find(step => step.order === state.openedStepOrder)
      : undefined
  );

  const getStartedModalOpen = useSelector<IState, false | 'sample' | 'upload'>(
    state => state.getStartedModalOpen
  );

  const [dontShowAgain, setDontShowAgain] = useState(false);

  useEffect(() => {
    if (
      !preference &&
      getStartedModalOpen &&
      !recordingErrorModalVisible &&
      !(
        recordingProcessingModalVisible ||
        (openedStep?.recording &&
          !openedStep?.recording?.transcriptResults &&
          !openedStep?.recording?.deleted)
      )
    ) {
      setOpen(true);
    }
  }, [
    preference,
    setOpen,
    getStartedModalOpen,
    recordingErrorModalVisible,
    recordingProcessingModalVisible,
    openedStep?.recording,
  ]);

  const handleClose = useCallback(() => {
    if (dontShowAgain) {
      updatePreference(true);
    }
    setOpen(false);
    dispatch(setGetStartedModalOpen(false));
  }, [setOpen, updatePreference, dontShowAgain, dispatch]);

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      footer={null}
      title={
        <div className={styles.getStartedModalTitle}>
          {getStartedModalOpen === 'sample'
            ? 'Play around and create your feed!'
            : 'Create Your Feed'}
        </div>
      }
    >
      {getStartedModalOpen === 'sample' ? (
        <p>
          In this step, you’ll click on words in the transcript to synchronize
          images, videos and more with your audio, resulting in a visual feed.{' '}
          <strong>Note:</strong> some pieces of content have already been added
          (which you can see on the right). Feel free to edit, delete, or move
          them around, and try adding a few of your own!
        </p>
      ) : (
        <p>
          In this step, you’ll click on words in the transcript to synchronize
          images, videos and more with your audio, resulting in a visual feed.
          This helps you underscore your points, guide further exploration, and
          deepen understanding for whatever it is you’re talking about.
        </p>
      )}
      <img
        src={image}
        width={446}
        height={262}
        alt="When I mention mountains I want to show them!"
      />
      <Flex
        justify={'center'}
        vertical={true}
        align={'center'}
        gap={'middle'}
        className={styles.getStartedModalButtons}
      >
        <Checkbox
          onChange={e => {
            setDontShowAgain(e.target.checked);
          }}
          defaultChecked={dontShowAgain}
        >
          Don't show again
        </Checkbox>
        <Button type={'primary'} size={'large'} onClick={handleClose}>
          Get started
        </Button>
      </Flex>
    </Modal>
  );
};
